import type {
  AgentCardData,
  ApprovalDecisionState,
  ApprovalItem,
  ApprovalRequestStatus,
  ChatStatus,
  ContextUsage,
  GeneratedFileData,
  Message,
  PendingApproval,
  ThinkingTreeData,
  ToolCallEvent,
  ToolCallStatus,
  TurnStopReason,
} from 'src/components/AnalyticsChat/types'

import type { SSEEvent } from './sse'

/*
 * Pure reducer for the agent chat wire contract
 * (api/src/lib/portfolioAgent/sse.ts). Every function here returns a new
 * message and never touches React, so the hook stays a thin transport and
 * the event semantics are unit-tested under the lib/ coverage thresholds.
 *
 *   conversation       { id, title }
 *   text-delta         { delta }
 *   thinking           { steps, finalized }
 *   query              { state }
 *   analytics-query-result  full rows of one runAnalyticsQuery (CSV export)
 *   cards              { cards }
 *   generated-file     { filename, mimeType, downloadUrl, sizeBytes }
 *   cost               ignored (no cost UI)
 *   context            { inputTokens, window } — once per model response
 *   tool-call          ToolCallEvent — upserted by id
 *   approval-request   PendingApproval — followed by done {awaitingApproval}
 *   approval-resolved  { pendingRunId, items: [{ callId, decision, alwaysAllow }] }
 *   error              { message, retryable? }
 *   done               { awaitingApproval, pendingRunId?, stopReason } | {}
 *
 * Unknown events and malformed payloads are ignored so the API can deploy a
 * new event before the web does.
 */

export type AssistantMessage = Extract<Message, { role: 'assistant' }>

export type TurnTerminal = 'done' | 'awaiting-approval' | 'error'

export interface TurnSignals {
  terminal?: TurnTerminal
  conversation?: { id: string; title: string | null }
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const str = (value: unknown, fallback = ''): string =>
  typeof value === 'string' ? value : fallback

const finiteNumber = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null

/** Cast one `context` payload at the boundary; null when unusable. */
export const parseContextUsage = (data: unknown): ContextUsage | null => {
  if (!isRecord(data)) return null
  const inputTokens = finiteNumber(data.inputTokens)
  const window = finiteNumber(data.window)
  if (inputTokens === null || window === null) return null
  if (inputTokens < 0 || window <= 0) return null
  return { inputTokens, window }
}

const TOOL_STATUSES: readonly ToolCallStatus[] = [
  'running',
  'awaiting-approval',
  'succeeded',
  'failed',
  'rejected',
  'refused',
]

const STOP_REASONS: readonly TurnStopReason[] = [
  'completed',
  'awaiting-approval',
  'step-budget',
  'max-turns',
  'deadline',
  'usd-budget',
  'interrupted',
]

const APPROVAL_STATUSES: readonly ApprovalRequestStatus[] = [
  'pending',
  'resolved',
  'expired',
  'superseded',
]

const DECISION_STATES: readonly ApprovalDecisionState[] = [
  'approved',
  'rejected',
  'superseded',
  'expired',
]

const asToolStatus = (value: unknown): ToolCallStatus | null =>
  TOOL_STATUSES.find((status) => status === value) ?? null

const asStopReason = (value: unknown): TurnStopReason | undefined =>
  STOP_REASONS.find((reason) => reason === value)

const asApprovalStatus = (value: unknown): ApprovalRequestStatus | undefined =>
  APPROVAL_STATUSES.find((status) => status === value)

const asDecisionState = (value: unknown): ApprovalDecisionState | undefined =>
  DECISION_STATES.find((state) => state === value)

/** Cast one `tool-call` payload at the boundary; null when unusable. */
export const parseToolCallEvent = (data: unknown): ToolCallEvent | null => {
  if (!isRecord(data)) return null
  const id = str(data.id)
  const name = str(data.name)
  const status = asToolStatus(data.status)
  if (!id || !name || !status) return null
  return {
    id,
    name,
    toolKey: str(data.toolKey),
    capability: str(data.capability, 'core'),
    connector: str(data.connector, 'twodots'),
    effect: data.effect === 'write' ? 'write' : 'read',
    status,
    title: str(data.title, name),
    argsPreview: str(data.argsPreview),
    startedAt: str(data.startedAt),
    ...(typeof data.durationMs === 'number'
      ? { durationMs: data.durationMs }
      : {}),
    ...(typeof data.resultPreview === 'string'
      ? { resultPreview: data.resultPreview }
      : {}),
  }
}

const parseApprovalItem = (data: unknown): ApprovalItem | null => {
  if (!isRecord(data)) return null
  const callId = str(data.callId)
  if (!callId) return null
  const preview = isRecord(data.preview) ? data.preview : {}
  const fields = Array.isArray(preview.fields)
    ? preview.fields.flatMap((field) =>
        isRecord(field) && typeof field.label === 'string'
          ? [
              {
                label: field.label,
                value: str(field.value),
                ...(field.markdown === true ? { markdown: true } : {}),
              },
            ]
          : []
      )
    : []
  const toolName = str(data.toolName)
  const decision = asDecisionState(data.decision)
  return {
    callId,
    toolName,
    toolKey: str(data.toolKey),
    capability: str(data.capability, 'core'),
    connector: str(data.connector, 'twodots'),
    title: str(data.title, toolName || 'Action'),
    argsPreview: str(data.argsPreview),
    preview: { fields, truncated: preview.truncated === true },
    alwaysAllowable: data.alwaysAllowable === true,
    scopeStatement: str(data.scopeStatement),
    ...(decision ? { decision } : {}),
    ...(typeof data.alwaysAllow === 'boolean'
      ? { alwaysAllow: data.alwaysAllow }
      : {}),
  }
}

/** Cast one `approval-request` payload (or a persisted approvalRequests row)
 *  at the boundary; null when unusable. */
export const parsePendingApproval = (data: unknown): PendingApproval | null => {
  if (!isRecord(data)) return null
  const pendingRunId = str(data.pendingRunId)
  const expiresAt = str(data.expiresAt)
  if (!pendingRunId || !expiresAt || !Array.isArray(data.items)) return null
  const items = data.items
    .map(parseApprovalItem)
    .filter((item): item is ApprovalItem => item !== null)
  const status = asApprovalStatus(data.status)
  return {
    pendingRunId,
    expiresAt,
    items,
    ...(status ? { status } : {}),
  }
}

const upsertToolEvent = (
  events: ToolCallEvent[] | undefined,
  incoming: ToolCallEvent
): ToolCallEvent[] => {
  const list = events ?? []
  const index = list.findIndex((event) => event.id === incoming.id)
  if (index === -1) return [...list, incoming]
  return list.map((event, i) =>
    i === index ? { ...event, ...incoming } : event
  )
}

const setToolStatus = (
  events: ToolCallEvent[] | undefined,
  callIds: Map<string, ToolCallStatus>
): ToolCallEvent[] | undefined =>
  events?.map((event) => {
    const status = callIds.get(event.id)
    return status && status !== event.status ? { ...event, status } : event
  })

/** Rows for approval items that never got their own tool-call frame. */
const toolEventsForApproval = (
  events: ToolCallEvent[] | undefined,
  approval: PendingApproval
): ToolCallEvent[] => {
  let next = events ?? []
  for (const item of approval.items) {
    next = upsertToolEvent(next, {
      id: item.callId,
      name: item.toolName,
      toolKey: item.toolKey,
      capability: item.capability,
      connector: item.connector,
      effect: 'write',
      status: 'awaiting-approval',
      title: item.title,
      argsPreview: item.argsPreview,
      startedAt: '',
    })
  }
  return next
}

/** Side channel the transport needs beyond the message patch. */
export function readTurnSignals({ event, data }: SSEEvent): TurnSignals {
  if (event === 'error') return { terminal: 'error' }
  if (event === 'conversation') {
    if (isRecord(data) && typeof data.id === 'string') {
      return {
        conversation: {
          id: data.id,
          title: typeof data.title === 'string' ? data.title : null,
        },
      }
    }
    return {}
  }
  if (event === 'done') {
    return {
      terminal:
        isRecord(data) && data.awaitingApproval === true
          ? 'awaiting-approval'
          : 'done',
    }
  }
  return {}
}

/** Apply one SSE event to the in-flight assistant message. */
export function applyAgentEvent(
  message: AssistantMessage,
  { event, data }: SSEEvent
): AssistantMessage {
  switch (event) {
    case 'conversation': {
      if (!isRecord(data) || typeof data.id !== 'string') return message
      return {
        ...message,
        conversationId: data.id,
        conversationTitle: typeof data.title === 'string' ? data.title : null,
      }
    }
    case 'text-delta': {
      const delta = isRecord(data) ? str(data.delta) : ''
      return delta ? { ...message, text: message.text + delta } : message
    }
    case 'thinking': {
      if (!isRecord(data) || !Array.isArray(data.steps)) return message
      return { ...message, thinking: data as unknown as ThinkingTreeData }
    }
    case 'query': {
      const state = isRecord(data) ? data.state : undefined
      return {
        ...message,
        queryState: state === 'querying' || state === 'done' ? state : 'idle',
      }
    }
    case 'analytics-query-result': {
      return {
        ...message,
        toolCalls: [
          ...(message.toolCalls ?? []),
          { name: 'runAnalyticsQuery', input: null, result: data },
        ],
      }
    }
    case 'cards': {
      const cards =
        isRecord(data) && Array.isArray(data.cards) ? data.cards : []
      return { ...message, cards: cards as AgentCardData[] }
    }
    case 'generated-file': {
      if (
        !isRecord(data) ||
        typeof data.filename !== 'string' ||
        typeof data.downloadUrl !== 'string'
      ) {
        return message
      }
      return {
        ...message,
        files: [...message.files, data as unknown as GeneratedFileData],
      }
    }
    case 'context': {
      const context = parseContextUsage(data)
      return context ? { ...message, context } : message
    }
    case 'tool-call': {
      const incoming = parseToolCallEvent(data)
      if (!incoming) return message
      return {
        ...message,
        toolEvents: upsertToolEvent(message.toolEvents, incoming),
      }
    }
    case 'approval-request': {
      const approval = parsePendingApproval(data)
      if (!approval) return message
      const others = (message.approvals ?? []).filter(
        (existing) => existing.pendingRunId !== approval.pendingRunId
      )
      return {
        ...message,
        approvals: [...others, approval],
        toolEvents: toolEventsForApproval(message.toolEvents, approval),
      }
    }
    case 'approval-resolved': {
      if (!isRecord(data) || !Array.isArray(data.items)) return message
      const pendingRunId = str(data.pendingRunId)
      const decisions = data.items.flatMap((item) => {
        if (!isRecord(item) || typeof item.callId !== 'string') return []
        const decision = asDecisionState(item.decision)
        return decision
          ? [
              {
                callId: item.callId,
                decision,
                alwaysAllow: item.alwaysAllow === true,
              },
            ]
          : []
      })
      return setApprovalDecisions(message, pendingRunId, decisions)
    }
    case 'error': {
      const text = isRecord(data) ? str(data.message) : ''
      return markFailed(message, {
        message: text || 'I couldn’t finish answering. Please try again.',
        retryable: isRecord(data) && data.retryable === true,
      })
    }
    case 'done': {
      const stopReason = isRecord(data)
        ? asStopReason(data.stopReason)
        : undefined
      // A pause for approvals is not the end of the turn: the tool list stays
      // live (matching the reopen path) until the resumed stream's own done.
      const paused = isRecord(data) && data.awaitingApproval === true
      return {
        ...message,
        done: Boolean(message.error) || !paused,
        ...(stopReason ? { stopReason } : {}),
      }
    }
    default:
      return message
  }
}

/** Close a failed turn without replacing any answer already received. */
export const markFailed = (
  message: AssistantMessage,
  error: NonNullable<AssistantMessage['error']>
): AssistantMessage => ({
  ...message,
  error,
  done: true,
  queryState: message.queryState === 'querying' ? 'idle' : message.queryState,
  ...(message.thinking
    ? { thinking: { ...message.thinking, finalized: true } }
    : {}),
  ...(message.toolEvents
    ? {
        toolEvents: message.toolEvents.map((event) =>
          event.status === 'running'
            ? { ...event, status: 'failed' as const }
            : event
        ),
      }
    : {}),
})

/**
 * Record decisions on one approval and mirror them onto the tool rows:
 * approved tools go back to running (the server executes them next),
 * everything else is closed out.
 */
export function setApprovalDecisions(
  message: AssistantMessage,
  pendingRunId: string,
  decisions: {
    callId: string
    decision: ApprovalDecisionState
    alwaysAllow?: boolean
  }[],
  patch: Partial<Pick<PendingApproval, 'status' | 'error'>> = {}
): AssistantMessage {
  const approvals = message.approvals ?? []
  if (!approvals.some((approval) => approval.pendingRunId === pendingRunId)) {
    return message
  }
  const byCallId = new Map(decisions.map((d) => [d.callId, d]))
  const toolStatuses = new Map<string, ToolCallStatus>()
  for (const decision of decisions) {
    toolStatuses.set(
      decision.callId,
      decision.decision === 'approved' ? 'running' : 'rejected'
    )
  }
  return {
    ...message,
    approvals: approvals.map((approval) => {
      if (approval.pendingRunId !== pendingRunId) return approval
      const next: PendingApproval = {
        ...approval,
        items: approval.items.map((item) => {
          const decision = byCallId.get(item.callId)
          if (!decision) return item
          return {
            ...item,
            decision: decision.decision,
            ...(decision.alwaysAllow !== undefined
              ? { alwaysAllow: decision.alwaysAllow }
              : {}),
          }
        }),
      }
      if ('status' in patch) {
        if (patch.status) next.status = patch.status
        else delete next.status
      }
      if ('error' in patch) {
        if (patch.error) next.error = patch.error
        else delete next.error
      }
      return next
    }),
    toolEvents: setToolStatus(message.toolEvents, toolStatuses),
  }
}

/** Undo an optimistic decision after the resume request failed. */
export function revertApprovalDecisions(
  message: AssistantMessage,
  pendingRunId: string,
  error?: string
): AssistantMessage {
  const approvals = message.approvals ?? []
  if (!approvals.some((approval) => approval.pendingRunId === pendingRunId)) {
    return message
  }
  const callIds = new Map<string, ToolCallStatus>()
  return {
    ...message,
    approvals: approvals.map((approval) => {
      if (approval.pendingRunId !== pendingRunId) return approval
      for (const item of approval.items) {
        callIds.set(item.callId, 'awaiting-approval')
      }
      const next: PendingApproval = {
        ...approval,
        items: approval.items.map((item) => {
          const { decision: _decision, alwaysAllow: _always, ...rest } = item
          return rest
        }),
      }
      if (error) next.error = error
      else delete next.error
      return next
    }),
    toolEvents: setToolStatus(message.toolEvents, callIds),
  }
}

/** Why the server answered 410 to a resume. */
export type GoneReason =
  | 'expired'
  | 'superseded'
  | 'resolved'
  | 'stale'
  | 'failed'

const GONE_MESSAGES: Partial<Record<GoneReason, string>> = {
  resolved:
    'This approval was already answered elsewhere. Reload the conversation to see the result.',
  stale:
    "The agent's tools changed since it asked, so this action did not run. Ask again to redo it.",
  failed:
    'This approval could not be resumed, so the action did not run. Ask again to redo it.',
}

/** Close an approval the server reported as gone (410) with the reason. */
export function settleApproval(
  message: AssistantMessage,
  pendingRunId: string,
  reason: GoneReason
): AssistantMessage {
  const approval = (message.approvals ?? []).find(
    (candidate) => candidate.pendingRunId === pendingRunId
  )
  if (!approval) return message
  if (reason === 'resolved') {
    return {
      ...setApprovalDecisions(message, pendingRunId, [], {
        status: 'resolved',
        error: GONE_MESSAGES.resolved,
      }),
      done: true,
    }
  }
  // The whole run is gone and nothing in it executed, so every item takes
  // the terminal state — including ones an optimistic patch had marked
  // approved. stale/failed are persisted server-side as expired.
  const decision: ApprovalDecisionState =
    reason === 'superseded' ? 'superseded' : 'expired'
  return {
    ...setApprovalDecisions(
      message,
      pendingRunId,
      approval.items.map((item) => ({ callId: item.callId, decision })),
      { status: decision, error: GONE_MESSAGES[reason] }
    ),
    done: true,
  }
}

/** An approval is open while nothing has been decided and it has not expired. */
export const isApprovalOpen = (
  approval: PendingApproval,
  now: number = Date.now()
): boolean =>
  (approval.status === undefined || approval.status === 'pending') &&
  !approval.items.some((item) => item.decision) &&
  Date.parse(approval.expiresAt) > now

/** Only the LAST approval on a message can still be open. */
export const openApprovalIn = (
  message: AssistantMessage,
  now: number = Date.now()
): PendingApproval | null => {
  const approvals = message.approvals ?? []
  const last = approvals[approvals.length - 1]
  return last && isApprovalOpen(last, now) ? last : null
}

/** The open approval on the last message of the conversation, if any. */
export const openApproval = (
  messages: Message[],
  now: number = Date.now()
): PendingApproval | null => {
  const last = messages[messages.length - 1]
  if (!last || last.role !== 'assistant') return null
  return openApprovalIn(last, now)
}

export const initialStatusFor = (
  messages: Message[],
  now: number = Date.now()
): ChatStatus => (openApproval(messages, now) ? 'awaiting-approval' : 'ready')

/** The user pressed Stop: close the message and fail anything still running. */
export const markStopped = (message: AssistantMessage): AssistantMessage => ({
  ...message,
  done: true,
  stopped: true,
  ...(message.thinking
    ? { thinking: { ...message.thinking, finalized: true } }
    : {}),
  ...(message.toolEvents
    ? {
        toolEvents: message.toolEvents.map((event) =>
          event.status === 'running' || event.status === 'awaiting-approval'
            ? { ...event, status: 'failed' as const }
            : event
        ),
      }
    : {}),
})
